export default class MapboxEvents {
  constructor({ main, opts }) {
    this._ = main;
    const { MapboxMap, mapboxConfiguration, mapboxModules } = opts;
    this.isDesktop = this._.sizes.isDesktop;
    this.mapboxConfiguration = mapboxConfiguration;
    this.mapboxModules = mapboxModules;
    // this.onClickMode = false;

    const map = MapboxMap.mapboxmap;

    this.hoveredFeature = {
      id: undefined,
      properties: {
        name: undefined,
      },
    };

    this.eventsType = {
      touchScrollEv: "ontouchstart" in window ? "touchstart" : "scroll",
      touchClickEv: "ontouchend" in window ? "touchstart" : "click",
    };
    this.tooltip = MapboxMap.tooltip;
    this.tooltip = MapboxMap.tooltip;
    this.map = map;

    // Detectar si es dispositivo táctil (móvil/tablet)
    this.isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;

    // Solo mostrar tooltip cuando el puntero está dentro del área activa del mapa
    this.mapHasPointer = false;

    // https://stackoverflow.com/a/33386309/4244812
    this.mousemove = this.mousemove.bind(this);
    this.mouseleave = this.mouseleave.bind(this);
    this.onMapContainerLeave = this.onMapContainerLeave.bind(this);
    this.onMapContainerEnter = this.onMapContainerEnter.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onMapOutOfViewport = this.onMapOutOfViewport.bind(this);

    map.on("load", () => {
      this._.spinner && this._.spinner.loadingSpinner(true);
      this._.map.sourceLayerManager.loadSources();
      this.bindMapContainerPointerEvents();
      this.bindViewportObserver();
    });

    map.on("render", (e) => {
      this._.map.spinner && this._.map.spinner.stopSpinner(e);
    });

    map.on("moveend", (e) => {
      if (this._.map.geoCoder.searchedLngLat) {
        this.applyGeocoderSearchResult(e);
      }

      // check if is over Canary Islands
      if (this._.map.controlCanaryIslands) {
        this._.map.controlCanaryIslands.checkIfMapsIsOverCanaryIslands(
          this._.map.mapboxmap
        );
      }
    });

    this.addButtonsEvents();

    // Configurar el evento de cierre del tooltip
    this.setupTooltipCloseEvent();

    // Ocultar tooltip al salir del viewport (pestaña oculta)
    this.bindVisibilityChange();

    this._.sizes.on("resize", () => {
      this.resize();
    });

    this.bindEvents();
  }

  applyGeocoderSearchResult(e) {
    const lngLat = this._.map.geoCoder.searchedLngLat;
    if (!lngLat) return;

    const point = this._.map.mapboxmap.project(lngLat);
    const features = this._.map.mapboxmap.queryRenderedFeatures(point, {
      layers: [this.mapboxConfiguration.hoverLayer.id],
    });

    e["point"] = point;
    e["features"] = features;

    if (features && features.length > 0) {
      this.mousemove(e, { fromGeocoder: true });
      this._.map.geoCoder.searchedLngLat = null;
      return;
    }

    // moveend puede dispararse antes de que termine el fly o se carguen los tiles.
    // Reintentar una sola vez cuando el mapa esté idle.
    if (this._geocoderIdlePending) return;
    this._geocoderIdlePending = true;

    const map = this.map;
    const tryOnIdle = () => {
      this._geocoderIdlePending = false;
      map.off("idle", tryOnIdle);
      if (!this._.map.geoCoder.searchedLngLat) return;

      const point2 = this._.map.mapboxmap.project(
        this._.map.geoCoder.searchedLngLat
      );
      const features2 = this._.map.mapboxmap.queryRenderedFeatures(point2, {
        layers: [this.mapboxConfiguration.hoverLayer.id],
      });

      if (features2 && features2.length > 0) {
        e["point"] = point2;
        e["features"] = features2;
        this.mousemove(e, { fromGeocoder: true });
      }
      this._.map.geoCoder.searchedLngLat = null;
    };
    map.once("idle", tryOnIdle);
  }

  onMapContainerLeave(e) {
    // En dispositivos táctiles (móvil/tablet): NO cerrar el tooltip por mouseleave.
    // El tooltip solo se cierra con el botón X. Esto evita el problema de iOS
    // donde el tooltip se cierra inmediatamente al abrirse.
    if (this.isTouchDevice) {
      return;
    }

    // En desktop: si el puntero "sale" hacia el tooltip, no cerrar.
    const relatedTarget = e && e.relatedTarget;
    if (
      relatedTarget &&
      typeof this._.el.contains === "function" &&
      this._.el.contains(relatedTarget)
    ) {
      return;
    }

    this._doMapContainerLeave();
  }

  _doMapContainerLeave() {
    this.mapHasPointer = false;
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
      this._pendingMousemove = null;
    }
    this.mouseleave("native");
  }

  onMapContainerEnter(e) {
    this.mapHasPointer = true;
  }

  onVisibilityChange() {
    if (document.visibilityState === "hidden") {
      this.mapHasPointer = false;
      this.mouseleave("native");
    }
  }

  bindMapContainerPointerEvents() {
    const canvasContainer = this.map.getCanvas?.()?.parentElement;
    if (!canvasContainer) return;
    canvasContainer.addEventListener("mouseleave", this.onMapContainerLeave);
    canvasContainer.addEventListener("mouseenter", this.onMapContainerEnter);
    // Guardar referencias para poder hacer removeEventListener si hace falta
    this._mapCanvasContainer = canvasContainer;
  }

  bindVisibilityChange() {
    if (typeof document.visibilityState !== "undefined") {
      document.addEventListener("visibilitychange", this.onVisibilityChange);
    }
  }

  onMapOutOfViewport(entries) {
    const entry = entries[0];
    if (entry && !entry.isIntersecting) {
      this.mapHasPointer = false;
      // En táctil solo cerrar si el mapa realmente salió del viewport (scroll largo)
      // No si es por interacción normal
      if (
        !this.isTouchDevice ||
        !this.tooltip.tooltipContent.classList.contains("active")
      ) {
        this.mouseleave("native");
      }
    }
  }

  bindViewportObserver() {
    // En dispositivos táctiles, no usar el observer para evitar cierres espurios
    if (this.isTouchDevice) return;

    const mapContainer = this._.map?.elMap;
    if (!mapContainer || typeof IntersectionObserver === "undefined") return;
    this._viewportObserver = new IntersectionObserver(this.onMapOutOfViewport, {
      threshold: 0,
      rootMargin: "0px",
    });
    this._viewportObserver.observe(mapContainer);
  }

  resize() {
    this.eventsType = {
      touchScrollEv: "ontouchstart" in window ? "touchstart" : "scroll",
      touchClickEv: "ontouchend" in window ? "touchstart" : "click",
    };

    if (this.tooltip) {
      this.mouseleave();
    }
  }

  addButtonsEvents() {
    this._.el
      .querySelectorAll(".buttons-panel .g-btn-group button")
      .forEach((button) => {
        button.addEventListener(this.eventsType.touchClickEv, (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._.map.spinner.loadingSpinner(true);
          this.updateMap.call(this, button);
        });
      });
    if (this._.el.querySelector(".buttons-panel select")) {
      this._.el
        .querySelector(".buttons-panel select")
        .addEventListener("change", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._.map.spinner.loadingSpinner(true);
          this.updateMap.call(this, e.target.value);
        });
    }
  }

  setupTooltipCloseEvent() {
    // Configurar el callback para el botón cerrar del tooltip
    if (this.tooltip) {
      this.tooltip.addCloseEvent(() => {
        this.mouseleave("native");
      }, this);
    }
  }

  updateMap(button) {
    const activeFilter =
      button.dataset !== undefined ? button.dataset.value : button;
    this._.settings.set({ activeFilter: activeFilter });

    this.handleButtonsClasses(button);
    this._.map.sourceLayerManager.setLayer(activeFilter);

    if (this._.settings.get("createLegend")) {
      this._.legend.updateLegend(activeFilter);
    }

    // if (this.onClickMode === true) {
    //   this._.map.events.updateFeatureAppearance(this.hoveredFeature);
    //   this._.map.tooltip.mouseover();
    //   const d = this._data.get(this.hoveredFeature.id);
    //   if (d !== undefined) {
    //     this.tooltip.mouseenter(
    //       Object.assign({ name: this.hoveredFeature.properties.name }, d)
    //     );
    //     this.addTooltipCloseEvs();
    //   } else {
    //     this.mouseleave();
    //   }
    // }
  }

  handleButtonsClasses(button) {
    if (button && button.tagName === undefined) return;
    this._.el.querySelectorAll("button").forEach(function (el) {
      el.classList.remove("active");
    });
    if (button !== undefined) button.classList.add("active");
  }

  bindEvents() {}

  bindMapEvents() {
    const { fullScreen } = this._.settings.getSettings();
    if (this.isDesktop === true || fullScreen === false) {
      this.addRemoveMapEvents({ remove: true });
      this.addRemoveMapEvents({ add: true });
    }
    return this;
  }

  addRemoveMapEvents({ remove, add }) {
    const { mapboxmap } = this._.map;
    if (add === true) {
      mapboxmap
        // .on(
        //   "click",
        //   this.mapboxConfiguration.hoverLayer.id,
        //   this.mousemove,
        //   true
        // )
        .on(
          "mousemove",
          this.mapboxConfiguration.hoverLayer.id,
          this.mousemove,
          true
        )
        .on(
          "mouseleave",
          this.mapboxConfiguration.hoverLayer.id,
          // this.mouseleave.bind(this, "native"),
          this.mouseleave,
          true
        );
      mapboxmap.dragPan.enable();
      mapboxmap.touchZoomRotate.enable();
    } else if (remove === true) {
      mapboxmap
        .off(
          "mousemove",
          this.mapboxConfiguration.hoverLayer.id,
          this.mousemove,
          true
        )
        .off(
          "mouseleave",
          this.mapboxConfiguration.hoverLayer.id,
          this.mouseleave,
          true
        );
      mapboxmap.dragPan.disable();
      mapboxmap.touchZoomRotate.disable();
    }
    return this;
  }

  resetHoveredFeature() {
    this.hoveredFeature = {
      id: undefined,
      properties: {
        name: undefined,
      },
    };
  }

  mouseleave(native) {
    // En dispositivos táctiles: NO cerrar el tooltip por eventos de la capa de Mapbox.
    // Solo cerrar cuando se llama explícitamente con "native" (botón X, visibilitychange, etc.)
    // IMPORTANTE: Mapbox pasa un objeto evento, solo aceptamos el string "native"
    const isExplicitClose = native === "native";
    if (this.isTouchDevice && !isExplicitClose) {
      return;
    }

    // El tooltip puede rechazar el cierre si está protegido
    const closed = this.tooltip.mouseleave();
    if (!closed) {
      return; // El tooltip rechazó el cierre, no hacer nada más
    }

    this.updateFeatureStroke(false);
    this.resetHoveredFeature();
    this._lastProcessedFeatureId = undefined;
    if (isExplicitClose) {
      if (this._.settings.get("createLegend")) {
        this._.legend.updateLegend(this._.settings.get("activeFilter"), true);
      }
    }
  }

  updateFeatureStroke(hover) {
    if (this.hoveredFeature.id !== undefined) {
      this.map.setFeatureState(
        {
          source: this.mapboxConfiguration.fillLayer.source,
          sourceLayer: this.mapboxConfiguration.fillLayer.sourceLayer,
          id: this.hoveredFeature.id,
        },
        {
          hover,
        }
      );
    }
  }

  updateHoveredFeature(feature) {
    // Primero remover el estado hover del feature anterior
    if (this.hoveredFeature && this.hoveredFeature.id) {
      this.updateFeatureStroke(false);
    }
    this.hoveredFeature = feature;
  }

  mouseover(feature) {
    this.updateFeatureStroke(false);
    this.updateHoveredFeature(feature);
    this.updateFeatureStroke(true);
  }

  // main method that is fired when user hovers the map
  mousemove(e, options = {}) {
    // Solo mostrar tooltip si el puntero está dentro del área activa del mapa
    // (salvo cuando viene del geocoder o en dispositivos táctiles)
    const fromGeocoder = options.fromGeocoder === true;
    if (!fromGeocoder && !this.isTouchDevice && !this.mapHasPointer) {
      this.mouseleave();
      return;
    }
    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      const featureId = feature.id;
      const previousFeatureId = this.hoveredFeature.id;

      if (previousFeatureId !== featureId) {
        this.mouseover(feature);

        const featureData = this._._data.get(featureId);
        if (featureData !== undefined) {
          // Crear un objeto temporal con el id anterior para la comparación en tooltip.hover
          const previousFeature =
            previousFeatureId !== undefined
              ? { id: previousFeatureId }
              : { id: undefined };
          this.tooltip.hover(featureId, featureData, previousFeature, e);
          if (this._.settings.get("createLegend")) {
            const activeFilter = this._.settings.get("activeFilter");
            this._.legend.updateLegend(activeFilter, false, featureData);
          }
        } else {
          // Si no hay datos, cerrar el tooltip
          this.mouseleave();
        }
      }
    } else {
      // Si no hay features, cerrar el tooltip
      this.mouseleave();
    }
  }

  // addTooltipCloseEvs() {
  //   this._.map.tooltip.addCloseEvent(restoreEvents, this);

  //   function restoreEvents() {
  //     this.addRemoveMapEvents({ remove: true, add: true });
  //     this.mouseleave();
  //     // this.updateClickMode(false);
  //   }
  // }

  // updateClickMode(onClickMode) {
  //   if (onClickMode === true) {
  //     this._.map.tooltip.tooltipContent.classList.add("click-mode");
  //     this.addTooltipCloseEvs();
  //   } else {
  //     this._.map.tooltip.tooltipContent.classList.remove("click-mode");
  //     this.mouseleave();
  //   }
  //   this.onClickMode = onClickMode;
  // }
}
